import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  bindScreeningCpf,
  createScreeningSession,
  getPublicScreeningModules,
  getScreeningNext,
  postScreeningAnswer,
  postScreeningAttachOrder,
  postScreeningBack,
  postScreeningContact,
  postScreeningDecline,
  postScreeningProgramOrder
} from './requests'

export const usePublicScreeningModules = () =>
  useQuery({
    queryKey: ['screening-public-modules'],
    queryFn: getPublicScreeningModules,
    staleTime: 60_000
  })

export const useScreeningNext = (token?: string) =>
  useQuery({
    queryKey: ['screening-next', token],
    queryFn: () => getScreeningNext(token as string),
    enabled: Boolean(token)
  })

export const useCreateScreeningSession = () =>
  useMutation({
    mutationFn: ({ heroSlug, moduleSlug }: { heroSlug: string; moduleSlug?: string }) =>
      createScreeningSession(heroSlug, moduleSlug)
  })

export const useScreeningContact = (token: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => postScreeningContact(token, payload),
    onSuccess: (data) => {
      client.setQueryData(['screening-next', token], data)
    }
  })
}

export const useScreeningAnswer = (token: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (payload: { key: string; value: unknown }) => postScreeningAnswer(token, payload),
    onSuccess: (data) => {
      client.setQueryData(['screening-next', token], data)
    }
  })
}

export const useCreateProgramOrder = () =>
  useMutation({
    mutationFn: (token: string) => postScreeningProgramOrder(token)
  })

export const useBindScreeningCpf = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ token, cpf }: { token: string; cpf: string }) => bindScreeningCpf(token, cpf),
    // O vínculo do CPF leva a sessão de "offer" para "awaiting_payment" na API; sem renovar, a trilha seguia com a fase
    // antiga em cache e, ao terminar o pagamento, voltava para a oferta.
    onSuccess: (_data, variables) => {
      client.invalidateQueries({ queryKey: ['screening-next', variables.token] })
    }
  })
}

export const useScreeningBack = (token: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => postScreeningBack(token),
    onSuccess: (data) => {
      client.setQueryData(['screening-next', token], data)
    }
  })
}

export const useScreeningDecline = (token: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (payload: { reasons: string[] }) => postScreeningDecline(token, payload),
    onSuccess: (data) => {
      client.setQueryData(['screening-next', token], data)
    }
  })
}

export const useScreeningAttachOrder = (token: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (payload: { order_id: number }) => postScreeningAttachOrder(token, payload),
    onSuccess: (data) => {
      client.setQueryData(['screening-next', token], data)
    }
  })
}
